/**
 * ClassificationSection 分類與識別 (S1.4, extended S2.1) — taxonomy path
 * (5-capable tree; the breadcrumb keeps the DD!17–18 Product Group / Sub Group
 * levels visible), subsidiary, external identifiers (NetSuite read-only Tier-1
 * mapping), Car Details fitment (incl. DD!31 variant / DD!32 extract year),
 * manufacturing (incl. DD!69 FA Sub Category), the C108 SYSTEM-COMPUTED Age
 * Group chip (fed by the pure `computeAgeGroup` — year inputs live in the edit
 * modal, never on the chip), and the customer part-number cross-refs.
 */

import { Descriptions, Space, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { buildBreadcrumb, useTaxonomyTree } from '@/hooks/useTaxonomy';
import { computeAgeGroup } from '@/lib/products/computeAgeGroup';
import type {
  Product,
  ProductCustomerPartNumber,
  ProductSparePartNumber,
} from '@/types/api/product';
import SectionHeader from './SectionHeader';

const SUBSIDIARY_LABEL_KEYS: Record<string, string> = {
  'SUB-TW-HOLIEN': 'products:wizard.subsidiaries.tw',
  'SUB-AU-SENDIT': 'products:wizard.subsidiaries.au',
};

interface ClassificationSectionProps {
  product: Product;
  onEdit: () => void;
}

export const ClassificationSection = ({ product, onEdit }: ClassificationSectionProps) => {
  const { t, i18n } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const isZh = i18n.language.startsWith('zh');
  const { nodes } = useTaxonomyTree();

  const breadcrumb = buildBreadcrumb(nodes, product.categoryId, isZh ? 'zh' : 'en');
  const subsidiaryKey = SUBSIDIARY_LABEL_KEYS[product.subsidiaryId];

  // C108 【MUST】 — Age Group is SYSTEM-COMPUTED from the user-entered vehicle
  // year range via the pure lib module (engine purity: no derivation logic here).
  const ageGroupCode = computeAgeGroup(
    product.ageStartYear,
    product.ageEndYear,
    new Date().getFullYear()
  );

  const fitmentYears = (product.fitment?.years ?? [])
    .map(
      (range) =>
        `${range.yearFrom}${range.yearTo ? `–${range.yearTo}` : ''}${
          range.variant ? ` (${range.variant})` : ''
        }`
    )
    .join(', ');

  const pnColumns: TableProps<ProductCustomerPartNumber>['columns'] = [
    {
      title: t('products:fields.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      render: (value: string) => renderDash(value),
    },
    {
      title: t('products:fields.partNumber'),
      dataIndex: 'partNumber',
      key: 'partNumber',
      render: (value: string) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{renderDash(value)}</span>
      ),
    },
    {
      title: t('products:fields.description'),
      dataIndex: 'description',
      key: 'description',
      render: (value?: string) => renderDash(value),
    },
  ];

  // 備件料號 (2026-08-27 §5) — party-less external identifier; no customer col.
  const sparePnColumns: TableProps<ProductSparePartNumber>['columns'] = [
    {
      title: t('products:fields.partNumber'),
      dataIndex: 'partNumber',
      key: 'partNumber',
      render: (value: string) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{renderDash(value)}</span>
      ),
    },
    {
      title: t('products:fields.description'),
      dataIndex: 'description',
      key: 'description',
      render: (value?: string) => renderDash(value),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <SectionHeader
          title={t('products:detail.sections.classification')}
          editLabel={t('common:actions.edit')}
          onEdit={onEdit}
          editDisabled={!product.isActive}
        />
        <Descriptions column={{ xs: 1, md: 2 }} layout="vertical" bordered>
          <Descriptions.Item label={t('products:fields.category')} span={2}>
            {renderDash(breadcrumb)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.subsidiary')}>
            {subsidiaryKey ? t(subsidiaryKey) : renderDash(product.subsidiaryId)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.includeChildren')}>
            {t(product.includeChildren ? 'products:detail.yes' : 'products:detail.no')}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.netSuiteItemId')}>
            {product.netSuiteItemId || t('products:notAssigned')}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.netSuiteSyncedAt')}>
            {product.netSuiteSyncedAt ? (
              <TimestampDisplay date={product.netSuiteSyncedAt} showLabel={false} />
            ) : (
              t('products:notSynced')
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.oemNo')}>
            {renderDash(product.oemNo)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.mpn')}>
            {renderDash(product.mpn)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.upcCode')}>
            {renderDash(product.upcCode)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.manufacturer')}>
            {renderDash(product.manufacturer)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.countryOfManufacture')}>
            {renderDash(product.countryOfManufacture)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.faSubCategory')}>
            {renderDash(product.faSubCategory)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.vehicleYears')}>
            {renderDash(
              product.ageStartYear != null && product.ageEndYear != null
                ? `${product.ageStartYear} – ${product.ageEndYear}`
                : undefined
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.ageGroup')}>
            {/* C108 — computed read-only chip; no edit affordance on the chip
                itself (year inputs live in EditClassificationModal). */}
            {ageGroupCode ? (
              <Tag style={{ margin: 0 }}>{t(`products:ageGroupBands.${ageGroupCode}`)}</Tag>
            ) : (
              renderDash(undefined)
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.variant')}>
            {renderDash(product.variant)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.extractYear')}>
            {renderDash(product.extractYear != null ? String(product.extractYear) : undefined)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.fitmentMakes')}>
            {renderDash(product.fitment?.makes.join(', '))}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.fitmentModels')}>
            {renderDash(product.fitment?.models.join(', '))}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.fitmentYears')} span={2}>
            {renderDash(fitmentYears)}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div>
        <Typography.Title level={5}>{t('products:fields.customerPartNumbers')}</Typography.Title>
        <TableContainer>
          <Table<ProductCustomerPartNumber>
            rowKey="id"
            size="small"
            dataSource={product.customerPartNumbers ?? []}
            columns={pnColumns}
            pagination={false}
          />
        </TableContainer>
      </div>

      <div>
        <Typography.Title level={5}>{t('products:fields.sparePartNumbers')}</Typography.Title>
        <TableContainer>
          <Table<ProductSparePartNumber>
            rowKey="id"
            size="small"
            dataSource={product.sparePartNumbers ?? []}
            columns={sparePnColumns}
            pagination={false}
          />
        </TableContainer>
      </div>
    </Space>
  );
};

export default ClassificationSection;
